import { Button, dialog } from "nq-component";

import BasePage from "../../../../../base/BasePage";
import CardView from "../../../../../components/common/CardView";
import { FloatingForm } from "../../../../../components/common/Converter";
import FormFactory from "../../../../../components/FormFactory";
import Progress from "../../../../../components/Progress";
import { upsertUseCase } from "../../../../../usecases/object";
import timesheetSchema from "../../../../HumanResource/TimeSheet/TimeSheetTable/timesheet_schema.json";

const timeFields = ["timeIn", "timeOut", "timePmIn", "timeAmOut"];

const timeFieldToModeField = {
  timeIn: "timeInMode",
  timeOut: "timeOutMode",
  timePmIn: "timePmInMode",
  timeAmOut: "timeAmOutMode",
};

/** Biometric log status: F1 = 0, F2 = 1, F3 = 2, F4 = 3 */
const statusToTimeField = {
  0: "timeIn",
  1: "timeOut",
  2: "timeAmOut",
  3: "timePmIn",
};

const statusToMode = { 0: "F1", 1: "F2", 2: "F3", 3: "F4" };

const modeToStatus = { F1: 0, F2: 1, F3: 2, F4: 3 };

export default class TimeFormPage extends BasePage {
  constructor(props) {
    super(props);
    this.state = {
      object: {},
      entryByTimeField: {},
    };
  }

  async componentDidMount() {
    const object = this.props.objectData;
    // Clone so form edits don't mutate the table's object (and change display)
    const clone =
      object && typeof object === "object"
        ? JSON.parse(JSON.stringify(object))
        : {};

    const entryByTimeField = {};
    const originalData = clone.originalData;
    if (Array.isArray(originalData)) {
      for (const entry of originalData) {
        const status = Number(entry?.status);
        const timeField = statusToTimeField[status];
        if (timeField == null) continue;
        entryByTimeField[timeField] = entry;
        const modeField = timeFieldToModeField[timeField];
        if (modeField && statusToMode[status]) {
          clone[modeField] = statusToMode[status];
        }
      }
    }

    this.setState({
      object: clone,
      entryByTimeField,
    });
  }

  handleDataChange(field, value) {
    this.setState((prevState) => ({
      object: { ...prevState.object, [field]: value },
    }));
  }

  getCollectionName() {
    return "biometric_logs";
  }
  computeCreditDeduction(undertimeMinutes) {
    let conversionValue = 0.0;

    for (let i = 1; i <= undertimeMinutes; i++) {
      // Add 0.003 at 7th, 19th, 31st, 43rd, 55th minute of every hour
      if (
        i % 60 === 7 ||
        i % 60 === 19 ||
        i % 60 === 31 ||
        i % 60 === 43 ||
        i % 60 === 55
      ) {
        conversionValue += 0.003;
      } else {
        conversionValue += 0.002;
      }
    }

    return conversionValue;
  }

  selectedDay = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  convertToMinutes(time) {
    // Expecting an object in the format { hours: 8, minutes: 0 }
    return time.hours * 60 + time.minutes || 0;
  }

  normalizeTimeToHHMMSS(timeStr) {
    if (!timeStr || timeStr === "--:--") return null;
    const parts = timeStr.trim().split(":");
    const h = parts[0]?.padStart(2, "0") ?? "00";
    const m = (parts[1] ?? "00").padStart(2, "0");
    const s = (parts[2] ?? "00").padStart(2, "0");
    return `${h}:${m}:${s}`;
  }

  async handleClickUpdate() {
    const { object, entryByTimeField } = this.state;
    if (!object?.date) return;

    const dateStr = object.date.includes("T")
      ? object.date.split("T")[0]
      : object.date;

    let updated = false;
    for (const timeField of timeFields) {
      const entry = entryByTimeField[timeField];
      if (!entry?.id) continue;

      const modeField = timeFieldToModeField[timeField];
      const timeVal = object[timeField];
      const normalized = this.normalizeTimeToHHMMSS(timeVal);
      const mode = object[modeField];
      const statusFromMode =
        mode != null && modeToStatus[mode] != null
          ? modeToStatus[mode]
          : undefined;

      const data = { id: entry.id, origin: "correction" };
      let hasChange = false;

      if (normalized) {
        data.timestamp = `${dateStr}T${normalized}.000Z`;
        hasChange = true;
      }
      if (statusFromMode != null && statusFromMode !== Number(entry.status)) {
        data.status = statusFromMode;
        hasChange = true;
      }

      if (!hasChange) continue;

      try {
        // console.log("Updating with data: ", data);
        await upsertUseCase().execute(this.getCollectionName(), data);
        updated = true;
      } catch (e) {
        this.showError(e?.message || e);
        return;
      }
    }

    if (updated) {
      this.props.handleCloseForm();
      this.props.refetchData();
    }
  }

  render() {
    const schema = timesheetSchema;
    console.log("schema: ", schema);
    console.log("OBJECT: ", this.state.object);
    if (!schema) return <Progress />;

    const modeFields = timeFields.map((key) => timeFieldToModeField[key]);
    const includeFields = Object.keys(schema.fields).filter(
      (key) => !["date", ...timeFields, ...modeFields].includes(key),
    );
    const { object } = this.state;
    timeFields.forEach((key) => {
      const hasTime = object[key] !== "--:--";
      schema.fields[key] = {
        type: "time",
        disabled: !hasTime,
      };
      const modeKey = timeFieldToModeField[key];
      schema.fields[modeKey] = {
        ...schema.fields[modeKey],
        disabled: !hasTime,
      };
    });

    schema.fields.date = {
      ...schema.fields.date,
      disabled: true,
    };
    return (
      <>
        <FloatingForm width="30rem">
          <CardView style={{ padding: 0 }}>
            <h4 className="fs-5 fw-bold pt-3 ps-3">Daily Time Record</h4>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                this.handleClickUpdate();
              }}
            >
              <hr />
              <div className="w-100" style={{ padding: "0 1rem" }}>
                <div className="w-100">
                  <FormFactory
                    className="pb-3"
                    schema={schema}
                    object={object}
                    onChange={this.handleDataChange.bind(this)}
                    excludeFields={includeFields}
                  />
                </div>
              </div>
              <hr />
              <div className="w-100 d-flex justify-content-end gap-4 mb-3 pe-3">
                <Button
                  type="button"
                  className="btn btn-light fs-sm"
                  onClick={() => {
                    this.props.handleCloseForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="btn btn-primary"
                  type="submit"
                  // onClick={this.handleClickUpdate.bind(this)}
                >
                  Update
                </Button>
              </div>
            </form>
          </CardView>
        </FloatingForm>
      </>
    );
  }
}
